import { lazy, Suspense, type ReactNode } from "react";
import { Landmark } from "lucide-react";
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  redirect,
  RouterProvider,
  useRouteLoaderData,
} from "react-router-dom";
import {
  ACCOUNTANT_ROLES,
  ADMIN_PORTAL_ROLES,
  ADMIN_ROLES,
  TEMPLATE_ADMIN_ROLES,
  PARTNER_ROLES,
} from "@/lib/auth";
import { api } from "@/lib/api";
import { ErrorBoundary } from "@/components/error-boundary";
import { ProtectedRoute } from "@/components/protected-route";
import { AdminShell, PartnerShell, AccountantShell, GenericShell } from "@/layouts/app-shell";
import { RootLayout } from "@/layouts/root-layout";
import type { AuthResponse } from "@/types/auth";

// ---------------------------------------------------------------------------
// Suspense fallback spinner
// ---------------------------------------------------------------------------
function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lazy-loaded pages — Public / Shared
// ---------------------------------------------------------------------------
const HomePage = lazy(() => import("@/pages/home-page").then(m => ({ default: (m as any).HomePage ?? (m as any).default })));
const LoginPage = lazy(() => import("@/pages/login-page").then(m => ({ default: (m as any).LoginPage ?? (m as any).default })));
const RegisterPage = lazy(() => import("@/pages/register-page").then(m => ({ default: (m as any).RegisterPage ?? (m as any).default })));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password-page").then(m => ({ default: (m as any).ForgotPasswordPage ?? (m as any).default })));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password-page").then(m => ({ default: (m as any).ResetPasswordPage ?? (m as any).default })));
const VerificationSuccessPage = lazy(() => import("@/pages/verification-success-page").then(m => ({ default: (m as any).VerificationSuccessPage ?? (m as any).default })));
const VerifyEmailPage = lazy(() => import("@/pages/verify-email-page").then(m => ({ default: (m as any).VerifyEmailPage ?? (m as any).default })));
const PrivacyPage = lazy(() => import("@/pages/privacy-page").then(m => ({ default: (m as any).PrivacyPage ?? (m as any).default })));
const TermsPage = lazy(() => import("@/pages/terms-page").then(m => ({ default: (m as any).TermsPage ?? (m as any).default })));
const OfflinePage = lazy(() => import("@/pages/offline-page").then(m => ({ default: (m as any).OfflinePage ?? (m as any).default })));
const UnauthorizedPage = lazy(() => import("@/pages/unauthorized-page").then(m => ({ default: (m as any).UnauthorizedPage ?? (m as any).default })));
const NotFoundPage = lazy(() => import("@/pages/not-found-page").then(m => ({ default: (m as any).NotFoundPage ?? (m as any).default })));
const ProfilePage = lazy(() => import("@/pages/profile-page").then(m => ({ default: (m as any).ProfilePage ?? (m as any).default })));
const OnboardingPage = lazy(() => import("@/pages/onboarding-page").then(m => ({ default: (m as any).OnboardingPage ?? (m as any).default })));
const RouteErrorPage = lazy(() => import("@/pages/route-error-page").then(m => ({ default: (m as any).RouteErrorPage ?? (m as any).default })));
const ServerErrorPage = lazy(() => import("@/pages/server-error-page").then(m => ({ default: (m as any).ServerErrorPage ?? (m as any).default })));
const DesignSystemPreviewPage = lazy(() => import("@/pages/design-system-preview-page").then(m => ({ default: (m as any).DesignSystemPreviewPage ?? (m as any).default })));

// ---------------------------------------------------------------------------
// Lazy-loaded pages — Admin
// ---------------------------------------------------------------------------
const AdminDashboardPage = lazy(() => import("@/pages/admin-dashboard-page").then(m => ({ default: (m as any).AdminDashboardPage ?? (m as any).default })));
const AdminFounderReviewPage = lazy(() => import("@/pages/admin-founder-review-page").then(m => ({ default: (m as any).AdminFounderReviewPage ?? (m as any).default })));
const AdminPropertiesPage = lazy(() => import("@/pages/admin-properties-page").then(m => ({ default: (m as any).AdminPropertiesPage ?? (m as any).default })));
const AdminPropertyCreatePage = lazy(() => import("@/pages/admin-property-create-page").then(m => ({ default: (m as any).AdminPropertyCreatePage ?? (m as any).default })));
const AdminPropertyDetailPage = lazy(() => import("@/pages/admin-property-detail-page").then(m => ({ default: (m as any).AdminPropertyDetailPage ?? (m as any).default })));
const AdminPropertyEditPage = lazy(() => import("@/pages/admin-property-edit-page").then(m => ({ default: (m as any).AdminPropertyEditPage ?? (m as any).default })));
const AdminPropertyImportPage = lazy(() => import("@/pages/admin-property-import-page").then(m => ({ default: (m as any).AdminPropertyImportPage ?? (m as any).default })));
const AdminPropertyTypesPage = lazy(() => import("@/pages/admin-property-types-page").then(m => ({ default: (m as any).AdminPropertyTypesPage ?? (m as any).default })));
const AdminPartnerBannersPage = lazy(() => import("@/pages/admin-partner-banners-page").then(m => ({ default: (m as any).AdminPartnerBannersPage ?? (m as any).default })));
const AdminSalesPage = lazy(() => import("@/pages/admin-sales-page").then(m => ({ default: (m as any).AdminSalesPage ?? (m as any).default })));
const AdminRefundsPage = lazy(() => import("@/pages/admin-refunds-page").then(m => ({ default: (m as any).AdminRefundsPage ?? (m as any).default })));
const AdminSaleDetailPage = lazy(() => import("@/pages/admin-sale-detail-page").then(m => ({ default: (m as any).AdminSaleDetailPage ?? (m as any).default })));
const AdminPartnersPage = lazy(() => import("@/pages/admin-partners-page").then(m => ({ default: (m as any).AdminPartnersPage ?? (m as any).default })));
const AdminPartnerDetailPage = lazy(() => import("@/pages/admin-partner-detail-page").then(m => ({ default: (m as any).AdminPartnerDetailPage ?? (m as any).default })));
const AdminBuyersPage = lazy(() => import("@/pages/admin-buyers-page").then(m => ({ default: (m as any).AdminBuyersPage ?? (m as any).default })));
const AdminBuyerDetailPage = lazy(() => import("@/pages/admin-buyer-detail-page").then(m => ({ default: (m as any).AdminBuyerDetailPage ?? (m as any).default })));
const AdminBuyerEditPage = lazy(() => import("@/pages/admin-buyer-edit-page").then(m => ({ default: (m as any).AdminBuyerEditPage ?? (m as any).default })));
const AdminCommissionsPage = lazy(() => import("@/pages/admin-commissions-page").then(m => ({ default: (m as any).AdminCommissionsPage ?? (m as any).default })));
const AdminCommissionProcessPage = lazy(() => import("@/pages/admin-commission-process-page").then(m => ({ default: (m as any).AdminCommissionProcessPage ?? (m as any).default })));
const AdminCommissionAnalyticsPage = lazy(() => import("@/pages/admin-commission-analytics-page").then(m => ({ default: (m as any).AdminCommissionAnalyticsPage ?? (m as any).default })));
const AdminCommissionWhtPage = lazy(() => import("@/pages/admin-commission-wht-page").then(m => ({ default: (m as any).AdminCommissionWhtPage ?? (m as any).default })));
const AdminCommissionDetailPage = lazy(() => import("@/pages/admin-commission-detail-page").then(m => ({ default: (m as any).AdminCommissionDetailPage ?? (m as any).default })));
const AdminUsersPage = lazy(() => import("@/pages/admin-users-page").then(m => ({ default: (m as any).AdminUsersPage ?? (m as any).default })));
const AdminUserDetailPage = lazy(() => import("@/pages/admin-user-detail-page").then(m => ({ default: (m as any).AdminUserDetailPage ?? (m as any).default })));
const AdminRolesPage = lazy(() => import("@/pages/admin-roles-page").then(m => ({ default: (m as any).AdminRolesPage ?? (m as any).default })));
const AdminRoleCreatePage = lazy(() => import("@/pages/admin-role-create-page").then(m => ({ default: (m as any).AdminRoleCreatePage ?? (m as any).default })));
const AdminRoleEditPage = lazy(() => import("@/pages/admin-role-edit-page").then(m => ({ default: (m as any).AdminRoleEditPage ?? (m as any).default })));
const AdminStaffPage = lazy(() => import("@/pages/admin-staff-page").then(m => ({ default: (m as any).AdminStaffPage ?? (m as any).default })));
const AdminStaffCreatePage = lazy(() => import("@/pages/admin-staff-create-page").then(m => ({ default: (m as any).AdminStaffCreatePage ?? (m as any).default })));
const AdminAnalyticsPage = lazy(() => import("@/pages/admin-analytics-page").then(m => ({ default: (m as any).AdminAnalyticsPage ?? (m as any).default })));
const AdminReportsPage = lazy(() => import("@/pages/admin-reports-page").then(m => ({ default: (m as any).AdminReportsPage ?? (m as any).default })));
const AdminActivityLogPage = lazy(() => import("@/pages/admin-activity-log-page").then(m => ({ default: (m as any).AdminActivityLogPage ?? (m as any).default })));
const AdminSettingsPage = lazy(() => import("@/pages/admin-settings-page").then(m => ({ default: (m as any).AdminSettingsPage ?? (m as any).default })));
const AdminSystemParametersPage = lazy(() => import("@/pages/admin-system-parameters-page").then(m => ({ default: (m as any).AdminSystemParametersPage ?? (m as any).default })));
const AdminNotificationsPage = lazy(() => import("@/pages/admin-notifications-page").then(m => ({ default: (m as any).AdminNotificationsPage ?? (m as any).default })));
const AdminMessagesPage = lazy(() => import("@/pages/admin-messages-page").then(m => ({ default: (m as any).AdminMessagesPage ?? (m as any).default })));
const AdminApiKeysPage = lazy(() => import("@/pages/admin-api-keys-page").then(m => ({ default: (m as any).AdminApiKeysPage ?? (m as any).default })));
const AdminBackupsPage = lazy(() => import("@/pages/admin-backups-page").then(m => ({ default: (m as any).AdminBackupsPage ?? (m as any).default })));
const AdminIntegrationsPage = lazy(() => import("@/pages/admin-integrations-page").then(m => ({ default: (m as any).AdminIntegrationsPage ?? (m as any).default })));
const AdminEmailTemplatesPage = lazy(() => import("@/pages/admin-email-templates-page").then(m => ({ default: (m as any).AdminEmailTemplatesPage ?? (m as any).default })));
const AdminDocumentTemplatesPage = lazy(() => import("@/pages/admin-document-templates-page").then(m => ({ default: (m as any).AdminDocumentTemplatesPage ?? (m as any).default })));
const AdminDocumentBuilderPage = lazy(() => import("@/pages/admin-document-builder-page").then(m => ({ default: (m as any).AdminDocumentBuilderPage ?? (m as any).default })));
const AdminDocumentBuilderCreatePage = lazy(() => import("@/pages/admin-document-builder-create-page").then(m => ({ default: (m as any).AdminDocumentBuilderCreatePage ?? (m as any).default })));
const AdminDocumentBuilderTemplatePage = lazy(() => import("@/pages/admin-document-builder-template-page").then(m => ({ default: (m as any).AdminDocumentBuilderTemplatePage ?? (m as any).default })));
const AdminFileManagementPage = lazy(() => import("@/pages/admin-file-management-page").then(m => ({ default: (m as any).AdminFileManagementPage ?? (m as any).default })));
const AdminExportHistoryPage = lazy(() => import("@/pages/admin-export-history-page").then(m => ({ default: (m as any).AdminExportHistoryPage ?? (m as any).default })));
const AdminPaginationLabPage = lazy(() => import("@/pages/admin-pagination-lab-page").then(m => ({ default: (m as any).AdminPaginationLabPage ?? (m as any).default })));

// ---------------------------------------------------------------------------
// Lazy-loaded pages — Partner
// ---------------------------------------------------------------------------
const PartnerHomePage = lazy(() => import("@/pages/partner-home-page").then(m => ({ default: (m as any).PartnerHomePage ?? (m as any).default })));
const PartnerDashboardPage = lazy(() => import("@/pages/partner-dashboard-page").then(m => ({ default: (m as any).PartnerDashboardPage ?? (m as any).default })));
const PartnerPropertiesPage = lazy(() => import("@/pages/partner-properties-page").then(m => ({ default: (m as any).PartnerPropertiesPage ?? (m as any).default })));
const PartnerPropertyDetailPage = lazy(() => import("@/pages/partner-property-detail-page").then(m => ({ default: (m as any).PartnerPropertyDetailPage ?? (m as any).default })));
const PartnerBuyersPage = lazy(() => import("@/pages/partner-buyers-page").then(m => ({ default: (m as any).PartnerBuyersPage ?? (m as any).default })));
const PartnerBuyerDetailPage = lazy(() => import("@/pages/partner-buyer-detail-page").then(m => ({ default: (m as any).PartnerBuyerDetailPage ?? (m as any).default })));
const PartnerBuyerEditPage = lazy(() => import("@/pages/partner-buyer-edit-page").then(m => ({ default: (m as any).PartnerBuyerEditPage ?? (m as any).default })));
const PartnerBuyerIntakePage = lazy(() => import("@/pages/partner-buyer-intake-page").then(m => ({ default: (m as any).PartnerBuyerIntakePage ?? (m as any).default })));
const PartnerBuyerCompletePage = lazy(() => import("@/pages/partner-buyer-complete-page").then(m => ({ default: (m as any).PartnerBuyerCompletePage ?? (m as any).default })));
const PartnerSalesPage = lazy(() => import("@/pages/partner-sales-page").then(m => ({ default: (m as any).PartnerSalesPage ?? (m as any).default })));
const PartnerSaleDetailPage = lazy(() => import("@/pages/partner-sale-detail-page").then(m => ({ default: (m as any).PartnerSaleDetailPage ?? (m as any).default })));
const PartnerSubmitSalePage = lazy(() => import("@/pages/partner-submit-sale-page").then(m => ({ default: (m as any).PartnerSubmitSalePage ?? (m as any).default })));
const PartnerCommissionsPage = lazy(() => import("@/pages/partner-commissions-page").then(m => ({ default: (m as any).PartnerCommissionsPage ?? (m as any).default })));
const PartnerCommissionDetailPage = lazy(() => import("@/pages/partner-commission-detail-page").then(m => ({ default: (m as any).PartnerCommissionDetailPage ?? (m as any).default })));
const PartnerCommissionReportPage = lazy(() => import("@/pages/partner-commission-report-page").then(m => ({ default: (m as any).PartnerCommissionReportPage ?? (m as any).default })));
const PartnerAccountPage = lazy(() => import("@/pages/partner-account-page").then(m => ({ default: (m as any).PartnerAccountPage ?? (m as any).default })));
const PartnerBankInfoPage = lazy(() => import("@/pages/partner-bank-info-page").then(m => ({ default: (m as any).PartnerBankInfoPage ?? (m as any).default })));
const PartnerDocumentsPage = lazy(() => import("@/pages/partner-documents-page").then(m => ({ default: (m as any).PartnerDocumentsPage ?? (m as any).default })));
const PartnerReferralsPage = lazy(() => import("@/pages/partner-referrals-page").then(m => ({ default: (m as any).PartnerReferralsPage ?? (m as any).default })));
const PartnerActivitiesPage = lazy(() => import("@/pages/partner-activities-page").then(m => ({ default: (m as any).PartnerActivitiesPage ?? (m as any).default })));
const PartnerAnalyticsPage = lazy(() => import("@/pages/partner-analytics-page").then(m => ({ default: (m as any).PartnerAnalyticsPage ?? (m as any).default })));
const PartnerInboxPage = lazy(() => import("@/pages/partner-inbox-page").then(m => ({ default: (m as any).PartnerInboxPage ?? (m as any).default })));
const PartnerNotificationsPage = lazy(() => import("@/pages/partner-notifications-page").then(m => ({ default: (m as any).PartnerNotificationsPage ?? (m as any).default })));
const PartnerProfileManagePage = lazy(() => import("@/pages/partner-profile-manage-page").then(m => ({ default: (m as any).PartnerProfileManagePage ?? (m as any).default })));
const PartnerSettingsPage = lazy(() => import("@/pages/partner-settings-page").then(m => ({ default: (m as any).PartnerSettingsPage ?? (m as any).default })));
const PartnerInvitePage = lazy(() => import("@/pages/partner-invite-page").then(m => ({ default: (m as any).PartnerInvitePage ?? (m as any).default })));

// ---------------------------------------------------------------------------
// Lazy-loaded pages — Buyer
// ---------------------------------------------------------------------------
const BuyerLoginPage = lazy(() => import("@/pages/buyer-login-page").then(m => ({ default: (m as any).BuyerLoginPage ?? (m as any).default })));
const BuyerDashboardPage = lazy(() => import("@/pages/buyer-dashboard-page").then(m => ({ default: (m as any).BuyerDashboardPage ?? (m as any).default })));
const BuyerSaleDetailPage = lazy(() => import("@/pages/buyer-sale-detail-page").then(m => ({ default: (m as any).BuyerSaleDetailPage ?? (m as any).default })));
const BuyerSubscriptionPage = lazy(() => import("@/pages/buyer-subscription-page").then(m => ({ default: (m as any).BuyerSubscriptionPage ?? (m as any).default })));
const BuyerVerifyPage = lazy(() => import("@/pages/buyer-verify-page").then(m => ({ default: (m as any).BuyerVerifyPage ?? (m as any).default })));

// ---------------------------------------------------------------------------
// Lazy-loaded pages — Accountant
// ---------------------------------------------------------------------------
const AccountantDashboardPage = lazy(() => import("@/pages/accountant-dashboard-page").then(m => ({ default: (m as any).AccountantDashboardPage ?? (m as any).default })));
const AccountantAnalyticsPage = lazy(() => import("@/pages/accountant-analytics-page").then(m => ({ default: (m as any).AccountantAnalyticsPage ?? (m as any).default })));
const AccountantReportsPage = lazy(() => import("@/pages/accountant-reports-page").then(m => ({ default: (m as any).AccountantReportsPage ?? (m as any).default })));
const AccountantMessagesPage = lazy(() => import("@/pages/accountant-messages-page").then(m => ({ default: (m as any).AccountantMessagesPage ?? (m as any).default })));
const AccountantDebtRecoveryPage = lazy(() => import("@/pages/accountant-debt-recovery-page").then(m => ({ default: (m as any).AccountantDebtRecoveryPage ?? (m as any).default })));
const AccountantNotificationsPage = lazy(() => import("@/pages/accountant-notifications-page").then(m => ({ default: (m as any).AccountantNotificationsPage ?? (m as any).default })));
const AdminDebtRecoveryPage = lazy(() => import("@/pages/accountant-debt-recovery-page").then(m => ({ default: (m as any).AdminDebtRecoveryPage ?? (m as any).default })));

// ---------------------------------------------------------------------------
// Profile shell — only show portal links the current user actually has
// permission to enter. Hardcoding all three (Profile / Admin / Partner)
// meant accountants and customer-care users would click "Admin" or
// "Partner" and bounce off the role guard with an unauthorized error.
// ---------------------------------------------------------------------------
// Roles that should see the /admin workspace link in the Profile sidebar.
// Includes ACCOUNTANT because they share admin-level routes even though
// their primary workspace is /accountant.
const ADMIN_PORTAL_PROFILE_ROLES = [
  "SUPER_ADMIN",
  "ADMIN",
  "FOUNDER",
  "CUSTOMER_CARE",
] as const;

function AccountantProfileHeader() {
  return (
    <div className="group relative overflow-hidden flex items-center gap-3 rounded-xl border p-3 shadow-xs backdrop-blur-md border-sidebar-border/50 bg-sidebar-accent/30 hover:bg-sidebar-accent/50 hover:border-sidebar-border/85 transition-all duration-300">
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/10 opacity-50 pointer-events-none" />
      <div className="flex size-10 shrink-0 items-center justify-center rounded-xl border shadow-2xs bg-gradient-to-tr from-emerald-500/20 to-teal-500/10 text-brand dark:text-teal-400 border-teal-500/30 transition-transform duration-300 group-hover:scale-105">
        <Landmark className="size-5" />
      </div>
      <div className="flex flex-col relative z-10">
        <span className="text-sm font-semibold leading-tight text-sidebar-foreground group-hover:text-brand transition-colors duration-300">
          Landnest Finance
        </span>
        <span className="text-sidebar-foreground/70 text-xs leading-tight font-medium mt-0.5 transition-colors duration-300 group-hover:text-sidebar-foreground/90">
          Accounting Workspace
        </span>
      </div>
    </div>
  );
}

function ProfileShell({ children }: { children: ReactNode }) {
  const rootData = useRouteLoaderData("root") as AuthResponse | undefined;
  const role = rootData?.user?.role;
  const isAccountant = role && (ACCOUNTANT_ROLES as readonly string[]).includes(role);
  const navItems: Array<{ to: string; label: string }> = [
    { to: "/profile", label: "Profile" },
  ];
  if (role && (ADMIN_PORTAL_PROFILE_ROLES as readonly string[]).includes(role)) {
    navItems.push({ to: "/admin/dashboard", label: "Admin" });
  }
  if (isAccountant) {
    navItems.push({ to: "/accountant", label: "Accountant" });
  }
  if (role && (PARTNER_ROLES as readonly string[]).includes(role)) {
    navItems.push({ to: "/partner/home", label: "Partner" });
  }
  return (
    <GenericShell
      title="Landnest Homes and Properties"
      description="Profile"
      navItems={navItems}
      headerSlot={isAccountant ? <AccountantProfileHeader /> : undefined}
    >
      {children}
    </GenericShell>
  );
}

// ---------------------------------------------------------------------------
// Loaders
// ---------------------------------------------------------------------------
async function rootLoader(): Promise<AuthResponse> {
  try {
    const response = await api.get<AuthResponse>("/auth/me");
    return { user: response.data.user };
  } catch {
    return { user: null };
  }
}

async function buyerSessionLoader() {
  try {
    await api.get("/buyer/session");
    return null;
  } catch {
    throw redirect("/buyer/login");
  }
}

// ---------------------------------------------------------------------------
// Suspense wrapper for route groups
// ---------------------------------------------------------------------------
function SuspenseOutlet() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Outlet />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------
const router = createBrowserRouter([
  {
    id: "root",
    path: "/",
    loader: rootLoader,
    element: <RootLayout />,
    HydrateFallback: LoadingSpinner,
    errorElement: (
      <Suspense fallback={<LoadingSpinner />}>
        <RouteErrorPage />
      </Suspense>
    ),
    children: [
      {
        path: "admin/document-builder/templates/:templateId",
        element: (
          <ProtectedRoute roles={TEMPLATE_ADMIN_ROLES}>
            <ErrorBoundary>
              <Suspense fallback={<LoadingSpinner />}>
                <AdminDocumentBuilderTemplatePage />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
      },
      // ---------------------------------------------------------------
      // Public routes
      // ---------------------------------------------------------------
      {
        index: true,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <HomePage />
          </Suspense>
        ),
      },
      {
        path: "login",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: "register",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <RegisterPage />
          </Suspense>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "reset-password",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
      {
        path: "verify-email",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <VerifyEmailPage />
          </Suspense>
        ),
      },
      {
        path: "verification-success",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <VerificationSuccessPage />
          </Suspense>
        ),
      },
      {
        path: "privacy",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PrivacyPage />
          </Suspense>
        ),
      },
      {
        path: "terms",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <TermsPage />
          </Suspense>
        ),
      },
      {
        path: "offline",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <OfflinePage />
          </Suspense>
        ),
      },
      {
        path: "unauthorized",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <UnauthorizedPage />
          </Suspense>
        ),
      },
      {
        path: "server-error",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ServerErrorPage />
          </Suspense>
        ),
      },

      // ---------------------------------------------------------------
      // Design system preview (admin-only)
      // ---------------------------------------------------------------
      ...(import.meta.env.DEV
        ? [
            {
              path: "design-system-preview",
              element: (
                <ProtectedRoute roles={ADMIN_PORTAL_ROLES}>
                  <Suspense fallback={<LoadingSpinner />}>
                    <DesignSystemPreviewPage />
                  </Suspense>
                </ProtectedRoute>
              ),
            },
          ]
        : []),

      // ---------------------------------------------------------------
      // Profile (any authenticated user)
      // ---------------------------------------------------------------
      {
        path: "profile",
        element: (
          <ProtectedRoute>
            <ProfileShell>
              <Suspense fallback={<LoadingSpinner />}>
                <ProfilePage />
              </Suspense>
            </ProfileShell>
          </ProtectedRoute>
        ),
      },

      // ---------------------------------------------------------------
      // Onboarding (partner)
      // ---------------------------------------------------------------
      {
        path: "onboarding",
        element: (
          <ProtectedRoute roles={PARTNER_ROLES}>
            <GenericShell
              title="Landnest Homes and Properties"
              description="Onboarding"
              navItems={[{ to: "/onboarding", label: "Current Step" }]}
            >
              <Suspense fallback={<LoadingSpinner />}>
                <OnboardingPage />
              </Suspense>
            </GenericShell>
          </ProtectedRoute>
        ),
      },

      // ---------------------------------------------------------------
      // Admin routes
      // ---------------------------------------------------------------
      {
        path: "admin",
        element: (
          <ProtectedRoute roles={ADMIN_ROLES}>
            <ErrorBoundary>
              <AdminShell>
                <SuspenseOutlet />
              </AdminShell>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/admin/dashboard" replace /> },
          { path: "dashboard", element: <AdminDashboardPage /> },
          {
            path: "founder-review",
            element: (
              <ProtectedRoute roles={["FOUNDER"]}>
                <AdminFounderReviewPage />
              </ProtectedRoute>
            ),
          },
          // Properties
          { path: "properties", element: <AdminPropertiesPage /> },
          { path: "properties/create", element: <AdminPropertyCreatePage /> },
          { path: "properties/:id", element: <AdminPropertyDetailPage /> },
          { path: "properties/:id/edit", element: <AdminPropertyEditPage /> },
          { path: "properties/import", element: <AdminPropertyImportPage /> },
          { path: "property-types", element: <AdminPropertyTypesPage /> },
          { path: "partner-banners", element: <AdminPartnerBannersPage /> },
          // Sales
          { path: "sales", element: <AdminSalesPage /> },
          { path: "refunds", element: <AdminRefundsPage /> },
          { path: "sales/submit", element: <PartnerSubmitSalePage /> },
          { path: "sales/:id", element: <AdminSaleDetailPage /> },
          // Partners
          { path: "partners", element: <AdminPartnersPage /> },
          { path: "partners/:id", element: <AdminPartnerDetailPage /> },
          // Buyers
          { path: "buyers", element: <AdminBuyersPage /> },
          { path: "buyers/:id", element: <AdminBuyerDetailPage /> },
          { path: "buyers/:id/edit", element: <AdminBuyerEditPage /> },
          // Commissions
          { path: "commissions", element: <AdminCommissionsPage /> },
          { path: "commissions/process", element: <AdminCommissionProcessPage /> },
          { path: "commissions/analytics", element: <AdminCommissionAnalyticsPage /> },
          { path: "commissions/wht", element: <AdminCommissionWhtPage /> },
          { path: "commissions/:commissionId", element: <AdminCommissionDetailPage /> },
          // Users & Roles & Staff
          { path: "users", element: <AdminUsersPage /> },
          { path: "users/:id", element: <AdminUserDetailPage /> },
          { path: "roles", element: <AdminRolesPage /> },
          { path: "roles/create", element: <AdminRoleCreatePage /> },
          { path: "roles/:id/edit", element: <AdminRoleEditPage /> },
          { path: "staff", element: <AdminStaffPage /> },
          { path: "staff/create", element: <AdminStaffCreatePage /> },
          // Analytics & Reports
          { path: "analytics", element: <AdminAnalyticsPage /> },
          { path: "reports", element: <AdminReportsPage /> },
          { path: "debt-recovery", element: <AdminDebtRecoveryPage /> },
          { path: "activity-log", element: <AdminActivityLogPage /> },
          // Settings & System
          { path: "settings", element: <AdminSettingsPage /> },
          { path: "system-parameters", element: <AdminSystemParametersPage /> },
          { path: "notifications", element: <AdminNotificationsPage /> },
          { path: "messages", element: <AdminMessagesPage /> },
          { path: "api-keys", element: <AdminApiKeysPage /> },
          { path: "backups", element: <AdminBackupsPage /> },
          { path: "integrations", element: <AdminIntegrationsPage /> },
          // Templates & Documents
          { path: "email-templates", element: <AdminEmailTemplatesPage /> },
          ...(import.meta.env.DEV
            ? [{ path: "pagination-lab", element: <AdminPaginationLabPage /> }]
            : []),
          {
            path: "document-templates",
            element: (
              <ProtectedRoute roles={TEMPLATE_ADMIN_ROLES}>
                <AdminDocumentTemplatesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "document-builder",
            element: (
              <ProtectedRoute roles={TEMPLATE_ADMIN_ROLES}>
                <AdminDocumentBuilderPage />
              </ProtectedRoute>
            ),
          },
          { path: "document-builder/create", element: <AdminDocumentBuilderCreatePage /> },
          {
            path: "documents",
            element: (
              <ProtectedRoute roles={TEMPLATE_ADMIN_ROLES}>
                <AdminDocumentBuilderPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "documents/*",
            element: (
              <ProtectedRoute roles={TEMPLATE_ADMIN_ROLES}>
                <AdminDocumentBuilderPage />
              </ProtectedRoute>
            ),
          },
          { path: "file-management", element: <AdminFileManagementPage /> },
          { path: "export-history", element: <AdminExportHistoryPage /> },
        ],
      },

      // ---------------------------------------------------------------
      // Partner routes
      // ---------------------------------------------------------------
      {
        path: "partner",
        element: (
          <ProtectedRoute roles={PARTNER_ROLES}>
            <ErrorBoundary>
              <PartnerShell>
                <SuspenseOutlet />
              </PartnerShell>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/partner/home" replace /> },
          { path: "home", element: <PartnerHomePage /> },
          { path: "dashboard", element: <PartnerDashboardPage /> },
          // Properties
          { path: "properties", element: <PartnerPropertiesPage /> },
          { path: "properties/:id", element: <PartnerPropertyDetailPage /> },
          // Buyers
          { path: "buyers", element: <PartnerBuyersPage /> },
          { path: "buyers/:id", element: <PartnerBuyerDetailPage /> },
          { path: "buyers/:id/edit", element: <PartnerBuyerEditPage /> },
          { path: "buyers/intake", element: <PartnerBuyerIntakePage /> },
          { path: "buyers/:buyerId/complete", element: <PartnerBuyerCompletePage /> },
          { path: "buyers/complete", element: <Navigate to="/partner/buyers/intake" replace /> },
          // Sales
          { path: "sales", element: <PartnerSalesPage /> },
          { path: "sales/:id", element: <PartnerSaleDetailPage /> },
          { path: "sales/submit", element: <PartnerSubmitSalePage /> },
          { path: "submit-sale", element: <PartnerSubmitSalePage /> },
          // Commissions
          { path: "commission-report", element: <Navigate to="/partner/analytics?tab=commission-report" replace /> },
          { path: "commissions", element: <PartnerCommissionsPage /> },
          { path: "commissions/:commissionId", element: <PartnerCommissionDetailPage /> },
          { path: "commissions/report", element: <PartnerCommissionReportPage /> },
          // Other
          { path: "account", element: <PartnerAccountPage /> },
          { path: "bank-info", element: <Navigate to="/partner/account?tab=banking" replace /> },
          { path: "documents", element: <PartnerDocumentsPage /> },
          { path: "referrals", element: <PartnerReferralsPage /> },
          { path: "activities", element: <Navigate to="/partner/inbox?tab=activity" replace /> },
          { path: "inbox", element: <PartnerInboxPage /> },
          { path: "analytics", element: <Navigate to="/partner/home?tab=analytics" replace /> },
          { path: "notifications", element: <Navigate to="/partner/inbox" replace /> },
          { path: "profile", element: <Navigate to="/partner/account" replace /> },
          { path: "settings", element: <Navigate to="/partner/account?tab=preferences" replace /> },
          { path: "invite", element: <Navigate to="/partner/referrals?tab=invite" replace /> },
        ],
      },

      // ---------------------------------------------------------------
      // Buyer routes
      // ---------------------------------------------------------------
      {
        path: "buyer",
        children: [
          {
            path: "login",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <BuyerLoginPage />
              </Suspense>
            ),
          },
          {
            path: "verify",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <BuyerVerifyPage />
              </Suspense>
            ),
          },
          {
            path: "subscribe/:token",
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <BuyerSubscriptionPage />
              </Suspense>
            ),
          },
          {
            element: (
              <ErrorBoundary>
                <GenericShell
                  title="Landnest Homes and Properties"
                  description="Buyer Portal"
                  navItems={[
                    { to: "/buyer/dashboard", label: "Dashboard" },
                  ]}
                >
                  <SuspenseOutlet />
                </GenericShell>
              </ErrorBoundary>
            ),
            loader: buyerSessionLoader,
            children: [
              { index: true, element: <Navigate to="/buyer/dashboard" replace /> },
              { path: "dashboard", element: <BuyerDashboardPage /> },
              { path: "sales/:id", element: <BuyerSaleDetailPage /> },
            ],
          },
        ],
      },

      // ---------------------------------------------------------------
      // Accountant routes
      // ---------------------------------------------------------------
      {
        path: "accountant",
        element: (
          <ProtectedRoute roles={ACCOUNTANT_ROLES}>
            <ErrorBoundary>
              <AccountantShell>
                <SuspenseOutlet />
              </AccountantShell>
            </ErrorBoundary>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/accountant/dashboard" replace /> },
          { path: "dashboard", element: <AccountantDashboardPage /> },
          { path: "analytics", element: <AccountantAnalyticsPage /> },
          { path: "reports", element: <AccountantReportsPage /> },
          { path: "debt-recovery", element: <AccountantDebtRecoveryPage /> },
          { path: "messages", element: <AccountantMessagesPage /> },
          { path: "notifications", element: <AccountantNotificationsPage /> },
          { path: "documents/create", element: <Navigate to="/accountant/dashboard" replace /> },
        ],
      },

      // ---------------------------------------------------------------
      // 404 catch-all
      // ---------------------------------------------------------------
      {
        path: "*",
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
